import { useState, useEffect } from 'react';
import { breakpoints } from '../styles/breakpoints';

export interface IWindowSize {
    width?: number;
    height?: number;
}

type Listener = (params: IWindowSize) => void;
let listeners: Set<Listener>;
let isListening = false;
let lastSize: IWindowSize;

export type TWindowBreakpoints = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const getSize = (isClient: boolean) => {
    lastSize = {
        width: isClient ? window.innerWidth : undefined,
        height: isClient ? window.innerHeight : undefined
    };
    return lastSize;
};

export function useSharedWindowSize() {
    const isClient = typeof window === 'object';
    startListening();

    const [windowSize, setWindowSize] = useState<IWindowSize>(lastSize);

    useEffect(() => {
        setWindowSize(getSize(isClient));
    }, [isClient]);

    useEffect(() => {
        if (!isClient) {
            return null;
        }

        function handleResize(size: IWindowSize) {
            setWindowSize(size);
        }

        addListener(handleResize);
        return () => removeListener(handleResize);
    }, [isClient]);

    return {
        ...getPayload(windowSize),
        setWindowSize
    };
}

const getBreakpoint = (width?: number): TWindowBreakpoints | undefined => {
    if (width === undefined) {
        return undefined;
    }
    if (width <= breakpoints.small) {
        return 'xs';
    }
    if (width <= breakpoints.medium) {
        return 'sm';
    }
    if (width <= breakpoints.large) {
        return 'md';
    }
    if (width <= breakpoints.xLarge) {
        return 'lg';
    }
    return 'xl';
};

export function useWindowSize() {
    const isClient = typeof window === 'object';

    const [windowSize, setWindowSize] = useState<IWindowSize>(() =>
        getSize(isClient)
    );

    useEffect(() => {
        if (!isClient) {
            return null;
        }

        setWindowSize(getSize(isClient));

        function handleResize() {
            setWindowSize(getSize(isClient));
        }

        addEvent('resize', handleResize);
        return () => removeEvent('resize', handleResize);
    }, [isClient]);

    return {
        ...getPayload(windowSize),
        setWindowSize
    };
}

function addListener(cb: Listener) {
    listeners.add(cb);
}

function removeListener(cb: Listener) {
    listeners.delete(cb);
}

function eventListener() {
    const size = getSize(true);
    for (const cb of listeners) {
        cb(size);
    }
}

function startListening() {
    const isClient = typeof window === 'object';

    if (isListening) {
        return;
    }

    isListening = true;

    listeners = new Set();
    getSize(isClient);

    addEvent('resize', eventListener);
}

export function stopListening() {
    listeners = null;
    isListening = false;
    removeEvent('resize', eventListener);
}

function getPayload(windowSize: IWindowSize) {
    return {
        windowSize,
        windowBreakpoint: getBreakpoint(windowSize.width),
        isUpToSmall: windowSize.width <= breakpoints.small,
        isSmallUp: windowSize.width > breakpoints.small,
        isMediumUp: windowSize.width > breakpoints.medium,
        isLargeUp: windowSize.width > breakpoints.large,
        isXLargeUp: windowSize.width > breakpoints.xLarge,
        isLowHeight: windowSize.height < 800
    };
}

function addEvent(type: string, cb: () => void) {
    if (typeof window == 'undefined') {
        return;
    }

    window.addEventListener(type, cb);
}

function removeEvent(type: string, cb: () => void) {
    if (typeof window == 'undefined') {
        return;
    }
    window.removeEventListener(type, cb);
}

export const sharedWindowSizeHandlers = {
    addListener,
    removeListener,
    getSize,
    startListening,
    getPayload
};
